import { Dispatch } from 'redux'
import { AxiosError, AxiosResponse } from 'axios'
import { camelize, snakeize } from 'casing'
import { UserList, UserState } from './reducer'
import {
  fetchUsers,
  fetchUsersFromIdentityService,
  updateUserData,
  updateUserFromIdentityService,
  fetchAPIKeys as fetchAPIKeysAPI,
  createAPIKey as createAPIKeyAPI,
  updateAPIKey as updateAPIKeyAPI,
  deleteAPIKey as deleteAPIKeyAPI,
  fetchAPIKeysLogs,
} from '../../../common/api/index'
import {
  ApiKeyDeleteSuccess,
  ApiKeyRequestSuccess,
  RequestFailureMessage,
} from '../../.../../../common/utils/messagesContants'
import {
  startLoading,
  stopLoading,
} from '../../../common/modules/loading/actions'
import { updateAppAlert } from '../../../common/modules/appAlert/actions'
import { InviteUserUpdateRequestSuccess } from '../../.../../../common/utils/messagesContants'
import { LoadingTypes } from '../../../common/modules/loading/reducer'
import { errorLogger } from '../../../common/utils/errorLogger'
import { RootState } from '../../../store'
import { getErrorMessages } from '../../../common/utils/error'
import { PartnerType } from '../../../common/modules/partner/action'
import { actionTypeWrapper } from '../../../common/utils/actionTypeWrapper'

import {
  APIKeysActionType,
  AuditLogsActionType,
  AuthProvider,
  UserListActionType,
} from './userListActionTypes'

export {
  APIKeysActionType,
  AuditLogsActionType,
  AuthProvider,
  UserListActionType,
}

export interface GetUsersParams {
  partnerId: string
  pageNumber?: number
  pageSize?: number
  authProvider: AuthProvider
}

const errMapping: Record<string, string> = {
  'The API returned an error: Api validation failed: suspendUser. Causes: errorSummary: Cannot suspend a user that is not active':
    'User already has the status provided.',
  'The API returned an error: Api validation failed: unsuspendUser. Causes: errorSummary: Cannot unsuspend a user that is not suspended':
    'User already has the status provided.',
}

export const getUsers =
  (args: GetUsersParams, partnerObjectType: PartnerType = PartnerType.User) =>
  async (dispatch: Dispatch, getState: () => RootState) => {
    try {
      const { pageNumber, pageSize } = args
      dispatch(startLoading(LoadingTypes.GET_USERS))
      let responseData: UserList
      if (args.authProvider == AuthProvider.AUTH0) {
        const { data } = await fetchUsersFromIdentityService(args)
        responseData = {
          count: data?.metadata?.total || 0,
          users:
            data?.data?.map((user: any) => ({
              id: user?.id || '',
              name: (user?.firstname || '') + ' ' + (user?.lastname || ''),
              email: user?.email || '',
              idpUserId: user?.idp_user_id || '',
              roles: user?.roles || [],
              status: user?.status,
              invitationStatus: user?.invitation_status,
              createdAt: user?.created_at || '',
              firstName: user?.firstname || '',
              lastName: user?.lastname || '',
            })) || [],
        }
      } else {
        const { data } = await fetchUsers(args)
        responseData = {
          count: data?.count || 0,
          users: data?.users || [],
        }
      }
      dispatch(
        setUsers(
          responseData,
          partnerObjectType,
          pageSize as number,
          pageNumber as number
        )
      )
    } catch (err: unknown) {
      const errMess = getErrorMessages([RequestFailureMessage])(
        (err as AxiosError<ErrorResponse>).response
      )
      const globalState = getState()
      errorLogger({ globalState })(err as Error)

      dispatch(setUsersErrorMess(errMess, partnerObjectType))
      dispatch(
        updateAppAlert({
          message: errMess,
          messageType: 'ERROR',
          autoClose: true,
        })
      )
    } finally {
      dispatch(stopLoading(LoadingTypes.GET_USERS))
    }
  }

export const updateUser =
  (
    partnerId: string,
    userId: string,
    data: Partial<UserState>,
    authProvider: AuthProvider,
    updateSuccessMsg?: string,
    partnerObjectType: PartnerType = PartnerType.User
  ) =>
  async (dispatch: Dispatch, getState: () => RootState) => {
    try {
      dispatch(startUserLoading())
      if (authProvider === AuthProvider.OKTA) {
        await updateUserData(partnerId, userId, snakeize(data))
      } else {
        await updateUserFromIdentityService(partnerId, snakeize(data), userId)
      }
      dispatch(
        updateAppAlert({
          message: updateSuccessMsg || InviteUserUpdateRequestSuccess,
          messageType: 'SUCCESS',
          autoClose: true,
          sidePanelAlert: true,
        })
      )
      return {
        success: true,
      }
    } catch (err) {
      const errMess =
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        ((err as AxiosError)?.response?.data as any)?.message ||
        RequestFailureMessage

      const globalState = getState()
      errorLogger({ globalState })(err as Error)

      dispatch(
        setUsersErrorMess(errMapping[errMess] || errMess, partnerObjectType)
      )
      dispatch(
        updateAppAlert({
          message: errMapping[errMess] || errMess,
          messageType: 'ERROR',
          autoClose: true,
          sidePanelAlert: true,
        })
      )
    } finally {
      dispatch(stopUserLoading())
    }
  }

export const fetchAPIKeys =
  (partnerId: string) =>
  async (dispatch: Dispatch, getState: () => RootState) => {
    try {
      dispatch(startLoading(LoadingTypes.API_KEY_READ))
      const { data } = await fetchAPIKeysAPI(partnerId)

      const responseData = {
        apikeys: data?.data?.map((key: any) => ({
          id: key?.id,
          name: key?.name,
          userId: key?.user_id,
          createdAt: key?.created_at,
          updatedAt: key?.updated_at,
          status: key?.status,
          role: key?.role === 'READER' ? 'Viewer' : 'Editor',
          clientId: key?.client_id,
          active: key?.active,
          enable: key?.enable,
          user: {
            id: key?.user?.id,
            firstName: key?.user?.firstname,
            lastName: key?.user?.lastname,
          },
        })),
      }

      dispatch({
        type: APIKeysActionType.FETCH_API_KEYS,
        payload: responseData.apikeys,
      })
    } catch (error: any) {
      const e = error as AxiosError
      const errorResponse = e?.response as AxiosResponse<ErrorResponse>

      dispatch(
        updateAppAlert({
          message: getErrorMessages([RequestFailureMessage])(errorResponse),
          messageType: 'ERROR',
          autoClose: true,
        })
      )
      const globalState = getState()
      errorLogger({ globalState })(
        error as Error,
        errorResponse?.data?.track_id
      )
    } finally {
      dispatch(stopLoading(LoadingTypes.API_KEY_READ))
    }
  }

export const createAPIKey =
  (partnerId: string, keyData: any) =>
  async (dispatch: Dispatch, getState: () => RootState) => {
    try {
      dispatch(startLoading(LoadingTypes.API_KEY_CREATE))
      const { data: response } = await createAPIKeyAPI(
        partnerId,
        snakeize(keyData)
      )

      const combinedData = {
        ...keyData,
        ...response,
        active: true,
        enable: true,
      }

      dispatch({
        type: APIKeysActionType.CREATE_API_KEY,
        payload: camelize(combinedData),
      })
      return camelize(response)
    } catch (error: any) {
      const e = error as AxiosError
      const errorResponse = e?.response as AxiosResponse<ErrorResponse>

      dispatch(
        updateAppAlert({
          message: getErrorMessages([RequestFailureMessage])(errorResponse),
          messageType: 'ERROR',
          autoClose: true,
        })
      )
      const globalState = getState()
      errorLogger({ globalState })(
        error as Error,
        errorResponse?.data?.track_id
      )
      throw error
    } finally {
      dispatch(stopLoading(LoadingTypes.API_KEY_CREATE))
    }
  }

export const updateAPIKey =
  (partnerId: string, keyId: string, keyData: any) =>
  async (dispatch: Dispatch, getState: () => RootState) => {
    try {
      dispatch(startLoading(LoadingTypes.API_KEY_UPDATE))
      const { data } = await updateAPIKeyAPI(
        partnerId,
        keyId,
        snakeize(keyData)
      )

      dispatch({ type: APIKeysActionType.UPDATE_API_KEY, payload: data })
      dispatch(
        updateAppAlert({
          message: ApiKeyRequestSuccess,
          messageType: 'SUCCESS',
          autoClose: true,
          sidePanelAlert: true,
        })
      )

      return data
    } catch (error: any) {
      const e = error as AxiosError
      const errorResponse = e?.response as AxiosResponse<ErrorResponse>

      dispatch(
        updateAppAlert({
          message: getErrorMessages([RequestFailureMessage])(errorResponse),
          messageType: 'ERROR',
          autoClose: true,
        })
      )
      const globalState = getState()
      errorLogger({ globalState })(
        error as Error,
        errorResponse?.data?.track_id
      )
    } finally {
      dispatch(stopLoading(LoadingTypes.API_KEY_UPDATE))
    }
  }

export const deleteAPIKey =
  (partnerId: string, keyId: string) =>
  async (dispatch: Dispatch, getState: () => RootState) => {
    try {
      dispatch(startLoading(LoadingTypes.API_KEY_DELETE))
      await deleteAPIKeyAPI(partnerId, keyId)
      dispatch(
        updateAppAlert({
          message: ApiKeyDeleteSuccess,
          messageType: 'SUCCESS',
          autoClose: true,
          sidePanelAlert: true,
        })
      )
    } catch (error: any) {
      const e = error as AxiosError
      const errorResponse = e?.response as AxiosResponse<ErrorResponse>

      dispatch(
        updateAppAlert({
          message: getErrorMessages([RequestFailureMessage])(errorResponse),
          messageType: 'ERROR',
          autoClose: true,
        })
      )
      const globalState = getState()
      errorLogger({ globalState })(
        error as Error,
        errorResponse?.data?.track_id
      )
    } finally {
      dispatch(stopLoading(LoadingTypes.API_KEY_DELETE))
    }
  }

export const fetchAuditLogs =
  (partnerId: string) =>
  async (dispatch: Dispatch, getState: () => RootState) => {
    dispatch(startLoading(LoadingTypes.API_KEY_LOGS))
    try {
      const { data } = await fetchAPIKeysLogs(partnerId)
      const response = camelize(data?.data) || []
      dispatch({
        type: AuditLogsActionType.FETCH_AUDIT_LOGS_SUCCESS,
        payload: response,
      })
      dispatch(stopLoading(LoadingTypes.API_KEY_LOGS))
    } catch (error: any) {
      const e = error as AxiosError
      const errorResponse = e?.response as AxiosResponse<ErrorResponse>

      dispatch(
        updateAppAlert({
          message: getErrorMessages(['Failed to fetch audit logs'])(
            errorResponse
          ),
          messageType: 'ERROR',
          autoClose: true,
        })
      )
      const globalState = getState()
      errorLogger({ globalState })(
        error as Error,
        errorResponse?.data?.track_id
      )
    }
  }

export const setUsers = (
  userList: UserList,
  partnerType: PartnerType = PartnerType.User,
  pageSize?: number,
  pageNumber?: number
) =>
  actionTypeWrapper(
    partnerType,
    setUsersWithoutWrapper(userList, pageSize, pageNumber)
  )
export const setUsersWithoutWrapper = (
  userList: UserList,
  pageSize?: number,
  pageNumber?: number
) => ({
  type: UserListActionType.SET_USERS,
  payload: { userList: camelize(userList), pageSize, pageNumber },
})

export const setUser = (
  userId: string,
  partnerType: PartnerType = PartnerType.User
) => actionTypeWrapper(partnerType, setUserWithoutWrapper(userId))

export const setUserWithoutWrapper = (userId: string) => ({
  type: UserListActionType.SET_USER,
  payload: userId,
})

export const setUserForBE = (
  user: Partial<UserState>,
  partnerType: PartnerType = PartnerType.User
) => actionTypeWrapper(partnerType, setUserForBEWithoutWrapper(user))

export const setUserForBEWithoutWrapper = (user: Partial<UserState>) => ({
  type: UserListActionType.SET_USER_FOR_BE,
  payload: user,
})

export const setUsersErrorMess = (
  message: string,
  partnerType: PartnerType = PartnerType.User
) => actionTypeWrapper(partnerType, setUserErrorMessWithoutWrapper(message))

export const setUserErrorMessWithoutWrapper = (message: string) => ({
  type: UserListActionType.SET_ERROR_MESS,
  payload: message,
})

export const setUsersPageSize = (
  pageSize: number,
  partnerType: PartnerType = PartnerType.User
) => actionTypeWrapper(partnerType, setUserPageSizeWithoutWrapper(pageSize))

export const setUserPageSizeWithoutWrapper = (pageSize: number) => ({
  type: UserListActionType.SET_USERS_LIMIT,
  payload: pageSize,
})

export const setUsersPageNumber = (
  pageNumber: number,
  partnerType: PartnerType = PartnerType.User
) =>
  actionTypeWrapper(partnerType, setUsersPageNumberWithoutWrapper(pageNumber))

export const setUsersPageNumberWithoutWrapper = (pageNumber: number) => ({
  type: UserListActionType.SET_USERS_PAGE_NUMBER,
  payload: pageNumber,
})

export const showUserSidePanel = (
  partnerType: PartnerType = PartnerType.User
) => actionTypeWrapper(partnerType, showUserSidePanelWithoutWrapper())

export const showUserSidePanelWithoutWrapper = () => ({
  type: UserListActionType.SET_OPEN_USER_PANEL,
})

export const hideUserSidePanel = (
  partnerType: PartnerType = PartnerType.User
) => actionTypeWrapper(partnerType, hideUserSidePanelWithoutWrapper())

export const hideUserSidePanelWithoutWrapper = () => ({
  type: UserListActionType.SET_CLOSE_USER_PANEL,
})

export const startUserLoading = (partnerType: PartnerType = PartnerType.User) =>
  actionTypeWrapper(partnerType, startUserLoadingWithoutWrapper())

export const startUserLoadingWithoutWrapper = () => ({
  type: UserListActionType.SET_START_USER_LOADING,
})
export const stopUserLoading = () => ({
  type: UserListActionType.SET_STOP_USER_LOADING,
})

export const clearUserDataBE = (partnerType: PartnerType = PartnerType.User) =>
  actionTypeWrapper(partnerType, clearUserDataBEWithoutWrapper())

export const clearUserDataBEWithoutWrapper = () => ({
  type: UserListActionType.SET_CLEAR_USER_BE,
})
